import { Dispatch, SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import { useI18n } from 'next-localization';

import { format, parse } from 'date-fns';
import { chunk, filter, isEmpty } from 'lodash-es';

import {
  AppointmentSearch,
  Timeslot,
  VIEW_TYPES,
  ViewTypeLink,
} from '@/features/book-appointment/booking.types';
import { useTimeslotChunksIndex } from '@/features/book-appointment/hooks/use-timeslot-chunks-index';
import { cn } from '@/lib/utils';
import { NeuButton, NeuFeedback, NeuIcon } from '@hcahealthcaredpx/neutron-react';

import styles from '../../../booking.module.css';
import {
  CHUNK_SIZE,
  FIRST_AVAILABLE_DAY_FORMAT_TOKEN,
  NEUTRON_DATEPICKER_FORMAT,
  NUMBER_DAYS_OUT,
} from '../constants';
import { getNextFormatted, parseDate } from '../utils';
import { DatePicker } from './date-picker';
import { NoTimeslots } from './no-timeslots';
import { TimePicker } from './time-picker';

interface CalendarProps {
  search: AppointmentSearch[];
  locationPhone: string;
  isLoading: boolean;
  handleNextScan: (nextStartDate: string) => void;
  selectedTimeslot: string;
  onTimeslotChange: Dispatch<Timeslot | null>;
}

const SLOT_BUFFER_MULTIPLIER = 3;

/**
 * Checks if we should fetch more slots based on current search length
 * @param searchLength - Current number of days in search
 * @returns boolean indicating if we should fetch more slots
 */
const shouldFetchMoreSlots = (searchLength: number): boolean => {
  return searchLength < parseInt(NUMBER_DAYS_OUT) * SLOT_BUFFER_MULTIPLIER;
};

/**
 * Triggers fetching next batch of appointment slots
 * @param search - Current appointment search data
 * @param handleNextScan - Callback to fetch more slots
 */
const fetchNextSlots = (
  search: AppointmentSearch[],
  handleNextScan: (date: string) => void
): void => {
  if (!shouldFetchMoreSlots(search.length)) {
    return;
  }

  const lastDay = search[search.length - 1].day;
  const nextDayFormatted = getNextFormatted(lastDay);
  handleNextScan(nextDayFormatted);
};

export const Calendar = ({
  search,
  isLoading,
  locationPhone,
  selectedTimeslot,
  onTimeslotChange,
  handleNextScan,
}: CalendarProps): JSX.Element => {
  const [viewType, setViewType] = useState(VIEW_TYPES.TIMESLOTS);
  const { index, handleNext, handlePrev, goToNextAvailable, goToChunk, resetIndex } =
    useTimeslotChunksIndex();

  // Note, the old implementation does something like:
  //
  // From the index of the date which contains timeslots take 5,
  // move these indexes and you get different portions of days,
  // e.g. and example would be to days.slice(36, 41),
  // but we don't have to play with slice indexes at all
  // we can just chunk it and then filter only the ones we're interested at...
  const daysInChunks = chunk(search, CHUNK_SIZE);

  // contains first chunk, as well as all others that have timeslots
  const chunksWithAvailableSlots = filter(daysInChunks, (chunk, i) => {
    // return first chunk always
    if (i === 0) return true;

    return chunk.some((day) => day.timeSlots !== null);
  });

  const visibleWeekdays = chunksWithAvailableSlots[index];
  const firstAvailableDay = useMemo(() => {
    if (!search || !search.length) {
      return null;
    }

    return search.find((x) => !isEmpty(x.timeSlots));
  }, [search]);

  const firstAvailableDayFormatted = useMemo(() => {
    if (firstAvailableDay?.day) {
      return format(parseDate(firstAvailableDay.day), FIRST_AVAILABLE_DAY_FORMAT_TOKEN);
    }

    return null;
  }, [firstAvailableDay]);

  const foundIndexFromFilteredChunks = chunksWithAvailableSlots.findIndex((chunk) =>
    chunk.some((x) => !isEmpty(x.timeSlots))
  );

  const { t } = useI18n();

  const changeViewTypeLink: ViewTypeLink = useMemo(() => {
    return {
      text:
        viewType === VIEW_TYPES.TIMESLOTS
          ? t('OAS.Booking.Appointment.TimeSlots.ViewCalendar')
          : t('OAS.Booking.Appointment.ViewTimes'),
      icon: viewType === VIEW_TYPES.TIMESLOTS ? 'calendar_today' : 'list',
    };
  }, [t, viewType]);

  const handleSetViewType = (e: SyntheticEvent) => {
    e.preventDefault();
    const type = viewType === VIEW_TYPES.CALENDAR ? VIEW_TYPES.TIMESLOTS : VIEW_TYPES.CALENDAR;
    setViewType(type);
  };

  const foundInNext = foundIndexFromFilteredChunks !== index && index === 0;

  useEffect(() => {
    if (index > chunksWithAvailableSlots.length - 1) {
      // if the current index is out of bounds, reset to last available chunk
      resetIndex();
    }
  }, [index, chunksWithAvailableSlots, resetIndex]);

  // if last chunk is not full we add the missing days for UI consistency
  if (chunksWithAvailableSlots.length > 0) {
    const lastChunk = chunksWithAvailableSlots[chunksWithAvailableSlots.length - 1];
    if (lastChunk.length < CHUNK_SIZE) {
      const missingDays = CHUNK_SIZE - lastChunk.length;
      for (let i = 0; i < missingDays; i++) {
        const lastDay = lastChunk[lastChunk.length - 1].day;
        const day = getNextFormatted(lastDay);
        lastChunk.push({
          day,
          timeSlots: [],
          appointmentTypeId: '',
          firstAvailable: '',
        });
      }
    }
  }

  const handleDateSelect = (date: string) => {
    const parsedDate = parse(date, NEUTRON_DATEPICKER_FORMAT, new Date());
    const formattedDate = format(parsedDate, 'yyyyMMdd');

    // check if date is in search
    const foundDate = search.find((x) => x.day === formattedDate);

    if (!date || !foundDate) return;
    goToChunk(chunksWithAvailableSlots)(date);
  };

  const calRef = useRef<HTMLDivElement>(null);
  const [displayedMonth, setDisplayedMonth] = useState<Date | null>(null);
  const displayedMonthRef = useRef<Date | null>(null);

  // Keep ref in sync with state for use in event handlers
  useEffect(() => {
    displayedMonthRef.current = displayedMonth;
  }, [displayedMonth]);

  // Monitor for next month button clicks in the Neutron DatePicker
  useEffect(() => {
    if (!calRef.current || viewType !== VIEW_TYPES.CALENDAR) return;

    const handleNextMonthClick = (e: MouseEvent) => {
      // Find the next month button in the Neutron DatePicker
      const nextButton = calRef.current?.querySelector('.label div>button:nth-of-type(2)');

      // Check if the button is rendered
      if (!nextButton || !e.target) return;

      // Check if user is clicking on next button
      if (nextButton !== e.target && !nextButton.contains(e.target as Node)) {
        return;
      }

      // Use a small delay to let the calendar update the displayed month first
      setTimeout(() => {
        if (!displayedMonthRef.current || !search.length) return;

        const lastDay = search[search.length - 1].day;
        const lastAvailableDate = parse(lastDay, 'yyyyMMdd', new Date());

        // Calculate what the next month will be after the click
        const currentDisplayed = displayedMonthRef.current;
        const nextMonth = new Date(
          currentDisplayed.getFullYear(),
          currentDisplayed.getMonth() + 1,
          1
        );

        // Check if the next month is beyond our last available data
        const isNextMonthAfterLastData =
          nextMonth.getFullYear() > lastAvailableDate.getFullYear() ||
          (nextMonth.getFullYear() === lastAvailableDate.getFullYear() &&
            nextMonth.getMonth() > lastAvailableDate.getMonth());

        if (isNextMonthAfterLastData) {
          fetchNextSlots(search, handleNextScan);
        }
      }, 100);
    };

    document.addEventListener('click', handleNextMonthClick);
    return () => {
      document.removeEventListener('click', handleNextMonthClick);
    };
  }, [viewType, search, handleNextScan]);

  return (
    <div className={cn(styles.calendarColumn, 'col-lg-5')}>
      <>
        {isLoading && (
          <div className={styles.calendarWrapper}>
            <div
              className={cn(
                'w-100 d-flex justify-content-center overflow-hidden',
                styles.loaderWrapper
              )}
            >
              <NeuFeedback kind="spinner" size="small" color="blue" />
            </div>
          </div>
        )}
        {!isLoading && (
          <>
            {search && firstAvailableDay && viewType === VIEW_TYPES.TIMESLOTS && (
              <div className={styles.calendarWrapper}>
                <TimePicker
                  selectedTimeslot={selectedTimeslot}
                  weekdays={visibleWeekdays}
                  onPrev={handlePrev}
                  onNext={() => {
                    handleNext(chunksWithAvailableSlots);

                    // if were on last index => get more slots
                    if (chunksWithAvailableSlots.length - 1 === index) {
                      fetchNextSlots(search, handleNextScan);
                    }
                  }}
                  onSelect={onTimeslotChange}
                />
                {/* A day with available appointments is found, and is not in the first week of searched */}
                {/* Todo: extract to comp */}
                {foundInNext && (
                  <div className="text-center mt-5">
                    <p>
                      {t('OAS.Booking.Appointment.Calendar.NextAppointmentText')}
                      <br />
                      <strong>{firstAvailableDayFormatted}</strong>
                    </p>
                    <NeuButton
                      kind="primary"
                      color="blue"
                      size="default"
                      onNeuClick={() => goToNextAvailable(foundIndexFromFilteredChunks)}
                    >
                      {t('OAS.Booking.Appointment.GoToNextAppointment')}
                    </NeuButton>
                  </div>
                )}
              </div>
            )}
            {search && firstAvailableDay && viewType === VIEW_TYPES.CALENDAR && (
              <div ref={calRef}>
                <DatePicker
                  search={search}
                  onDateSelect={handleDateSelect}
                  onDateSelectCallback={() => setViewType(VIEW_TYPES.TIMESLOTS)}
                  onMonthChange={setDisplayedMonth}
                />
              </div>
            )}
            {!firstAvailableDay && <NoTimeslots locationPhone={locationPhone} />}
          </>
        )}
      </>

      {search && firstAvailableDay && (
        <div className="text-center mt-3 mb-3">
          <Link href="#" style={{ textDecoration: 'none' }} onClick={handleSetViewType}>
            <NeuIcon
              name={changeViewTypeLink.icon}
              color="blue"
              fill={true}
              weight={400}
              size="1x"
            />{' '}
            {changeViewTypeLink.text}
          </Link>
        </div>
      )}
    </div>
  );
};

Calendar.displayName = 'Calendar';
